import type * as React from "react";
import { Eye, Pencil } from "lucide-react";
import { EmptyState } from "@/ui/molecules";
import { cn } from "@/utils/utils";
import type { TAsyncState } from "@/store";
import { fileTypeLabel } from "./fileTypeLabel";
import { offeredSlotTypes, toTypeSlug, type TSubmissionContext } from "./submissionContext";
import type { TFileReviewDetails } from "./types";

export type TFileReviewTabId =
    | "review"
    | "etl"
    | "generated-gl"
    | "operating-statement"
    | "original"
    | "history"
    | "comparison"
    | "supporting";

/**
 * Every view the page can show, with the name it is referred to by.
 *
 * The Review view is offered once per file type under its own labels, so its
 * entry here only names the view; every other label is also the radio's text
 * inside its tab. This is the source of the valid `?tab=` values.
 */
export const FILE_REVIEW_TABS: { id: TFileReviewTabId; label: string }[] = [
    { id: "review", label: "Review" },
    { id: "etl", label: "ETL" },
    { id: "generated-gl", label: "Generated GL" },
    { id: "operating-statement", label: "Operating statement" },
    { id: "original", label: "Original files" },
    { id: "history", label: "Submission history" },
    { id: "comparison", label: "Compare movement" },
    { id: "supporting", label: "Supporting files" },
];

const TAB_IDS = new Set<string>(FILE_REVIEW_TABS.map((tab) => tab.id));

/** Anything unrecognised — including a hand-typed or stale `?tab=` value, or `null` — falls back to "review". */
export function parseTabId(raw: string | null): TFileReviewTabId {
    if (raw !== null && TAB_IDS.has(raw)) {
        return raw as TFileReviewTabId;
    }
    return "review";
}

const TAB_LABELS = Object.fromEntries(FILE_REVIEW_TABS.map((tab) => [tab.id, tab.label])) as Record<
    TFileReviewTabId,
    string
>;

/**
 * GL is generated only from a TB-only deal's trial balance, so only those files
 * have a Generated GL view to offer. An opening trial balance never generates GL.
 */
function offersGeneratedGL(file: TFileReviewDetails | null): file is TFileReviewDetails {
    return (
        file !== null && file.dealFileType === "tb_only" && file.fileType !== "open_trial_balance"
    );
}

/**
 * An operating statement is the income-statement view of a trial balance, so only
 * a processed TB has one — not an OTB (opening balances), a GL or a FinJournal.
 * Behind the `operating_statement_enabled` company flag while it rolls out.
 */
function offersOperatingStatement(
    file: TFileReviewDetails | null,
    enabled: boolean,
): file is TFileReviewDetails {
    return enabled && file !== null && file.fileType === "trial_balance";
}

/** Statuses with no trustworthy figures yet (still processing) or thrown out by the workflow. */
const OPERATING_STATEMENT_BLOCKED_STATUSES: ReadonlySet<string> = new Set([
    "not_received",
    "uploaded",
    "under_acceptance",
    "rejected",
    "failed",
]);

/**
 * The statement is only built from a TB without errors: it has lines, none of them
 * is invalid (warnings are fine), and the workflow has neither still got it in
 * processing nor thrown it out. The backend enforces the same rule (409).
 */
export function isOperatingStatementAvailable(file: TFileReviewDetails): boolean {
    return (
        file.totalLineItems > 0 &&
        file.invalidLineItemCount === 0 &&
        !OPERATING_STATEMENT_BLOCKED_STATUSES.has(file.status)
    );
}

const OPERATING_STATEMENT_UNAVAILABLE_HINT = "Available once the Trial Balance has no errors";

type TNavOption = {
    key: string;
    label: string;
    live: boolean;
    /** Why a greyed option is unavailable, shown on hover. */
    hint?: string;
    checked: boolean;
    onChange: () => void;
    testId: string | undefined;
};

export type TFileReviewTabsProps = {
    /** `null` for a restricted caller, who never loads a file. */
    file: TFileReviewDetails | null;
    context: TAsyncState<TSubmissionContext>;
    activeTab: TFileReviewTabId;
    onSelectTab: (tab: TFileReviewTabId) => void;
    /** A supporting-files-only caller: exactly one view is theirs to see. */
    restricted?: boolean;
    /** The backend `FileType` wire value currently being reviewed — the selected type, not necessarily the open file's. */
    selectedFileType: string;
    /** A file type picked from the Prepare file tab, as a backend `FileType` wire value. */
    onSelectFileType: (fileType: string) => void;
    /** Review caps for TB/GL/OTB types; a type the user cannot review renders disabled rather than ejecting them from the page. */
    tbglCaps: { canReview: boolean };
    /** Review caps for FinJournal. See `tbglCaps`. */
    finjournalCaps: { canReview: boolean };
    /** The company's `operating_statement_enabled` flag. Off hides the option entirely. */
    operatingStatementEnabled?: boolean;
    /**
     * The trial balance the Comparison view would compare, or `null` when the view is
     * not offered (the user lacks the `comparison_tab` surface, or the submission has
     * no TB). Computed once by the page so the option and the view it mounts cannot
     * disagree — and it is the TB's id even while a sibling file is open.
     */
    comparisonFileId?: string | null;
    /** The selected view's content, rendered inside the panel the tabs sit on. */
    children?: React.ReactNode;
};

type TTabGroupId = "prepare" | "original" | "etl" | "reports";

type TTabGroup = {
    id: TTabGroupId;
    label: string;
    icon: typeof Pencil;
    options: TNavOption[];
};

/** Which tab owns each view; the Review view is the tab's per-type radios. */
const GROUP_OF_VIEW: Record<TFileReviewTabId, TTabGroupId> = {
    review: "prepare",
    comparison: "prepare",
    original: "original",
    supporting: "original",
    history: "original",
    etl: "etl",
    "generated-gl": "etl",
    "operating-statement": "reports",
};

const PANEL_ID = "file-review-panel";
const tabButtonId = (id: TTabGroupId): string => `file-review-group-${id}`;

/**
 * The submission's navigation as folder tabs over one panel: Prepare file holds one
 * review radio per file type the submission expects plus Comparison, and the other
 * tabs hold the views that only show the submission. The active tab's options sit
 * in a "Show in table" row at the top of the panel, with the selected view's content
 * (the `children`) below it.
 *
 * The per-type radios are live by review capability, so a user can switch to a
 * sibling type's review without leaving the page. All radios share one name, and
 * only the active tab's are rendered, so exactly one view is checked.
 */
export function FileReviewTabs(props: TFileReviewTabsProps): React.ReactElement {
    const {
        file,
        context,
        activeTab,
        onSelectTab,
        restricted = false,
        selectedFileType,
        onSelectFileType,
        tbglCaps,
        finjournalCaps,
        operatingStatementEnabled = false,
        comparisonFileId = null,
        children,
    } = props;

    const data = context.status === "rejected" ? null : context.data;
    const offeredTypes = offeredSlotTypes(data);
    // The selection always gets an option to land on, because `checked` strictly
    // follows `selectedFileType` and never the anchor — leave it unrepresented and the
    // group renders with nothing checked. Two ways it can be missing from the slots:
    // before the context arrives there are no slots at all (a cold mount of a `?type=`
    // link), and afterwards an OTB or FJ selection can outlive its slot, which exists
    // only in months where such a file does. Appending keeps the backend's display
    // order intact — only OTB and FJ can vanish, and both already sort last.
    const perTypeOptions: string[] = [
        ...new Set(
            [
                ...(offeredTypes.length > 0 ? offeredTypes : file !== null ? [file.fileType] : []),
                selectedFileType,
            ].filter((type) => type !== ""),
        ),
    ];
    // An absent or still-loading context must grey the ETL option, not default it
    // live — there is nothing yet to say a generated export exists.
    const etlAvailable = data !== null && data.etlArtifacts.some((artifact) => artifact.available);

    // `checked` must mean *this exact view is showing* — nothing weaker. Checking a
    // radio for a view that is not showing makes it unreachable, because clicking an
    // already-checked radio fires no change event. A greyed option is never checked.
    const viewOption = (tab: TFileReviewTabId, live: boolean): TNavOption => ({
        key: tab,
        label: TAB_LABELS[tab],
        live,
        checked: live && activeTab === tab,
        onChange: () => onSelectTab(tab),
        testId: live ? `file-review-tab-${tab}` : undefined,
    });

    const reviewOptions: TNavOption[] = perTypeOptions.map((fileType) => {
        // Null-guarded for the restricted caller, who has no open file.
        const isOpenFileType = file !== null && fileType === file.fileType;
        const canReviewType =
            fileType === "financial_journals" ? finjournalCaps.canReview : tbglCaps.canReview;
        return {
            key: fileType,
            label: fileTypeLabel(fileType),
            // The open file's own type is always live: the user is already reviewing
            // it, so the shell has necessarily let them through, and disabling it would
            // strand them with no checked radio in the row. Every other type is gated
            // on whether this user may review that type at all.
            live: isOpenFileType || canReviewType,
            // The selected *type*, never the open file's — during a month with no file
            // of the selected type the anchor file is a different type, and checking
            // the anchor's radio there would contradict the empty state below it.
            checked: activeTab === "review" && fileType === selectedFileType,
            onChange: () => {
                onSelectFileType(fileType);
                onSelectTab("review");
            },
            // The open file's option keeps the entrance testid, so tab-selection
            // tests address the review view the same way as every other; sibling types
            // get their own, individually addressable one.
            testId: isOpenFileType
                ? "file-review-tab-review"
                : `file-review-type-${toTypeSlug(fileType) ?? fileType}`,
        };
    });

    // A restricted caller has exactly one view, so the others are not rendered
    // disabled — they are not theirs to see at all.
    const groups: TTabGroup[] = restricted
        ? [
              {
                  id: "original",
                  label: "Original files",
                  icon: Eye,
                  options: [viewOption("supporting", true)],
              },
          ]
        : [
              {
                  id: "prepare",
                  label: "Prepare file",
                  icon: Pencil,
                  options: [
                      ...reviewOptions,
                      ...(comparisonFileId !== null ? [viewOption("comparison", true)] : []),
                  ],
              },
              {
                  id: "original",
                  label: "Original files",
                  icon: Eye,
                  options: [
                      viewOption("original", true),
                      viewOption("supporting", true),
                      viewOption("history", true),
                  ],
              },
              {
                  id: "etl",
                  label: "ETL files",
                  icon: Eye,
                  options: [
                      viewOption("etl", etlAvailable),
                      // Greyed rather than hidden until GL is generated, so the row does not
                      // change shape under the user when the Generate GL run completes.
                      ...(offersGeneratedGL(file)
                          ? [viewOption("generated-gl", file.hasGLGeneratedLines)]
                          : []),
                  ],
              },
              {
                  id: "reports",
                  label: "Financial reports",
                  icon: Eye,
                  options: offersOperatingStatement(file, operatingStatementEnabled)
                      ? [
                            {
                                ...viewOption(
                                    "operating-statement",
                                    isOperatingStatementAvailable(file),
                                ),
                                hint: OPERATING_STATEMENT_UNAVAILABLE_HINT,
                            },
                        ]
                      : [],
              },
          ];

    // A tab with nothing to offer is not shown at all.
    const tabs = groups.filter((group) => group.options.length > 0);
    // A deep link can name a view whose tab is not offered; the first tab then stands in,
    // with nothing checked, rather than leaving the panel without a header.
    const activeGroup = tabs.find((group) => group.id === GROUP_OF_VIEW[activeTab]) ?? tabs[0];

    const selectGroup = (group: TTabGroup): void => {
        if (group === activeGroup) return;
        // Prepare file lands on the selected type's review, not merely the first type's.
        const selectedReview =
            group.id === "prepare"
                ? group.options.find((option) => option.key === selectedFileType && option.live)
                : undefined;
        if (selectedReview !== undefined) {
            onSelectTab("review");
            return;
        }
        group.options.find((option) => option.live)?.onChange();
    };

    return (
        <div>
            <div role="tablist" className="flex flex-wrap items-end gap-1">
                {tabs.map((group) => {
                    const isActive = group === activeGroup;
                    const isDisabled = group.options.every((option) => !option.live);
                    const isPrepare = group.id === "prepare";

                    return (
                        <button
                            key={group.id}
                            type="button"
                            role="tab"
                            id={tabButtonId(group.id)}
                            data-testid={tabButtonId(group.id)}
                            aria-selected={isActive}
                            aria-controls={PANEL_ID}
                            disabled={isDisabled}
                            title={
                                isDisabled
                                    ? group.options.find((option) => option.hint)?.hint
                                    : undefined
                            }
                            onClick={() => selectGroup(group)}
                            className={cn(
                                "relative flex min-w-48 items-center justify-center gap-2 rounded-t-lg border px-4 py-2 text-sm font-medium transition-colors",
                                isActive
                                    ? "-mb-px border-border border-b-background bg-background"
                                    : "border-border bg-muted hover:bg-muted/70",
                                isPrepare
                                    ? isActive
                                        ? "text-primary"
                                        : "text-primary/60"
                                    : isActive
                                      ? "text-foreground"
                                      : "text-muted-foreground",
                                isDisabled && "cursor-not-allowed opacity-50",
                            )}
                        >
                            <group.icon className="h-4 w-4" aria-hidden />
                            {group.label}
                        </button>
                    );
                })}
            </div>

            <div
                role="tabpanel"
                id={PANEL_ID}
                aria-labelledby={
                    activeGroup !== undefined ? tabButtonId(activeGroup.id) : undefined
                }
                className="rounded-b-lg rounded-tr-lg border border-border bg-background"
            >
                <div
                    data-testid="file-review-show-in-table"
                    className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3"
                >
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Show in table:
                    </span>
                    {(activeGroup?.options ?? []).map((option) => (
                        <label
                            key={option.key}
                            title={!option.live ? option.hint : undefined}
                            className={cn(
                                "flex items-center gap-2 text-sm",
                                option.live ? "cursor-pointer" : "cursor-not-allowed opacity-50",
                            )}
                        >
                            <input
                                type="radio"
                                name="file-review-view"
                                className="h-4 w-4 accent-primary"
                                checked={option.checked}
                                disabled={!option.live}
                                onChange={option.onChange}
                                {...(option.testId !== undefined
                                    ? { "data-testid": option.testId }
                                    : {})}
                            />
                            <span className={cn(option.checked && "font-medium text-foreground")}>
                                {option.label}
                            </span>
                        </label>
                    ))}
                </div>
                <div className="border-t border-border p-4">{children}</div>
            </div>
        </div>
    );
}

// Derived from the view names so a rename cannot leave the placeholder copy behind.
// "review" never reaches this component in practice — the shell only renders it for
// the other views — but the shared `TFileReviewTabId` keeps the prop type honest.
const PLACEHOLDER_COPY: Record<TFileReviewTabId, string> = Object.fromEntries(
    FILE_REVIEW_TABS.map((tab) => [tab.id, `${tab.label} — coming soon.`]),
) as Record<TFileReviewTabId, string>;

export type TFileReviewPlaceholderProps = { tab: TFileReviewTabId };

export function FileReviewTabPlaceholder(props: TFileReviewPlaceholderProps): React.ReactElement {
    const { tab } = props;

    return (
        <div data-testid={`file-review-tab-placeholder-${tab}`}>
            <EmptyState title={PLACEHOLDER_COPY[tab]} />
        </div>
    );
}
